import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { type CreditPackRow, DEFAULT_CREDIT_PACK_ROWS } from '@masonic-tools/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { showSuccess, showError } from '@/lib/swal';
import { ArrowLeft, ChevronDown, ChevronUp, Package, Plus, Trash2 } from 'lucide-react';

const PACK_ID_PATTERN = /^[a-z][a-z0-9_]{0,62}$/;

type DraftPack = {
  id: string;
  credits: string;
  pricePounds: string;
  label: string;
  description: string;
};

function toDrafts(rows: CreditPackRow[]): DraftPack[] {
  return rows.map((r) => ({
    id: r.id,
    credits: String(r.credits),
    pricePounds: (r.pricePence / 100).toFixed(2),
    label: r.label ?? '',
    description: r.description ?? '',
  }));
}

function parseDrafts(drafts: DraftPack[]): { ok: true; packs: CreditPackRow[] } | { ok: false; message: string } {
  const ids = new Set<string>();
  const packs: CreditPackRow[] = [];

  for (let i = 0; i < drafts.length; i++) {
    const d = drafts[i];
    const id = d.id.trim().toLowerCase().replace(/\s+/g, '_');
    if (!id) {
      return { ok: false, message: `Row ${i + 1}: Pack ID is required.` };
    }
    if (!PACK_ID_PATTERN.test(id)) {
      return {
        ok: false,
        message: `Row ${i + 1}: Pack ID must start with a letter and contain only lowercase letters, digits, and underscores.`,
      };
    }
    if (ids.has(id)) {
      return { ok: false, message: `Duplicate pack ID: ${id}` };
    }
    ids.add(id);

    const credits = Number.parseInt(d.credits.trim(), 10);
    if (!Number.isFinite(credits) || credits < 1 || credits > 1_000_000) {
      return { ok: false, message: `Row ${i + 1}: Credits must be a whole number between 1 and 1,000,000.` };
    }

    const pounds = Number.parseFloat(d.pricePounds.trim().replace(/,/g, ''));
    if (!Number.isFinite(pounds) || pounds < 0) {
      return { ok: false, message: `Row ${i + 1}: Price must be a valid non-negative amount in GBP.` };
    }
    const pricePence = Math.round(pounds * 100);
    if (pricePence < 30) {
      return {
        ok: false,
        message: `Row ${i + 1}: Price must be at least £0.30 (Stripe minimum for GBP card payments).`,
      };
    }

    const label = d.label.trim();
    const description = d.description.trim();

    packs.push({
      id,
      credits,
      pricePence,
      ...(label ? { label } : {}),
      ...(description ? { description } : {}),
    });
  }

  if (packs.length === 0) {
    return { ok: false, message: 'Add at least one credit pack, or use Restore defaults.' };
  }

  return { ok: true, packs };
}

export default function CreditPacksPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState<DraftPack[]>(() => toDrafts(DEFAULT_CREDIT_PACK_ROWS));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'systemSettings', 'platform'));
      const raw = snap.data()?.creditPacks;
      if (Array.isArray(raw) && raw.length > 0) {
        const cleaned: CreditPackRow[] = [];
        for (const item of raw) {
          if (
            item &&
            typeof item === 'object' &&
            typeof (item as CreditPackRow).id === 'string' &&
            typeof (item as CreditPackRow).credits === 'number' &&
            typeof (item as CreditPackRow).pricePence === 'number'
          ) {
            const row = item as CreditPackRow;
            cleaned.push({
              id: row.id,
              credits: row.credits,
              pricePence: row.pricePence,
              ...(typeof row.label === 'string' ? { label: row.label } : {}),
              ...(typeof row.description === 'string' ? { description: row.description } : {}),
            });
          }
        }
        if (cleaned.length > 0) {
          setDrafts(toDrafts(cleaned));
          return;
        }
      }
      setDrafts(toDrafts(DEFAULT_CREDIT_PACK_ROWS));
    } catch (e) {
      console.error(e);
      showError('Failed to load credit packs.');
      setDrafts(toDrafts(DEFAULT_CREDIT_PACK_ROWS));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateDraft = (index: number, patch: Partial<DraftPack>) => {
    setDrafts((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const move = (index: number, dir: -1 | 1) => {
    setDrafts((prev) => {
      const j = index + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const removeRow = (index: number) => {
    setDrafts((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const addRow = () => {
    setDrafts((prev) => [
      ...prev,
      {
        id: `pack_${Date.now()}`,
        credits: '25',
        pricePounds: '15.00',
        label: 'New pack',
        description: '',
      },
    ]);
  };

  const handleSave = async () => {
    const parsed = parseDrafts(drafts);
    if (!parsed.ok) {
      showError(parsed.message);
      return;
    }
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'systemSettings', 'platform'),
        { creditPacks: parsed.packs },
        { merge: true },
      );
      showSuccess('Credit packs saved. Dining will pick them up on next load.');
      setDrafts(toDrafts(parsed.packs));
    } catch (e) {
      console.error(e);
      showError('Failed to save credit packs.');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = () => {
    setDrafts(toDrafts(DEFAULT_CREDIT_PACK_ROWS));
    showSuccess('Form reset to default packs. Save to write them to Firestore.');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} className="w-fit">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Platform settings
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
            Dashboard
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Package className="h-7 w-7" />
              Credit packs
            </CardTitle>
            <CardDescription>
              Define purchasable Masonic Tools credit bundles: internal ID, credits granted, GBP price, short title, and
              optional description. Minimum price £0.30 per pack (Stripe).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <>
                <div className="space-y-6">
                  {drafts.map((row, index) => (
                    <div
                      key={`${index}-${row.id}`}
                      className="rounded-lg border bg-card p-4 space-y-4 shadow-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium text-muted-foreground">Pack {index + 1}</span>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            disabled={index === 0}
                            onClick={() => move(index, -1)}
                            aria-label="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            disabled={index === drafts.length - 1}
                            onClick={() => move(index, 1)}
                            aria-label="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            disabled={drafts.length <= 1}
                            onClick={() => removeRow(index)}
                            aria-label="Remove pack"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor={`pack-id-${index}`}>Pack ID</Label>
                          <Input
                            id={`pack-id-${index}`}
                            value={row.id}
                            onChange={(e) => updateDraft(index, { id: e.target.value })}
                            placeholder="pack_50"
                            autoComplete="off"
                          />
                          <p className="text-xs text-muted-foreground">
                            Stable identifier sent to checkout. Lowercase letters, digits, underscores.
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`pack-label-${index}`}>Title</Label>
                          <Input
                            id={`pack-label-${index}`}
                            value={row.label}
                            onChange={(e) => updateDraft(index, { label: e.target.value })}
                            placeholder="50 credits"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`pack-credits-${index}`}>Credits</Label>
                          <Input
                            id={`pack-credits-${index}`}
                            inputMode="numeric"
                            value={row.credits}
                            onChange={(e) => updateDraft(index, { credits: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`pack-price-${index}`}>Price (GBP)</Label>
                          <Input
                            id={`pack-price-${index}`}
                            inputMode="decimal"
                            value={row.pricePounds}
                            onChange={(e) => updateDraft(index, { pricePounds: e.target.value })}
                            placeholder="28.00"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`pack-desc-${index}`}>Description (optional)</Label>
                        <Textarea
                          id={`pack-desc-${index}`}
                          value={row.description}
                          onChange={(e) => updateDraft(index, { description: e.target.value })}
                          placeholder="Shown on the unit credits page under the pack selector."
                          rows={3}
                          className="resize-y min-h-[72px]"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="secondary" onClick={addRow}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add pack
                  </Button>
                  <Button type="button" variant="outline" onClick={handleRestoreDefaults}>
                    Restore defaults to form
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2 border-t pt-6">
                  <Button type="button" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save to Firestore'}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
